import type { DiscoverFeedItem } from "@/domain/gems-game/domain/lib/Discover";
import {
  type Action,
  type BaseAnalyticsExtras,
  type ControlType,
  type InteractionType,
  TrackObject,
  useAnalyticsTracker,
} from "./AnalyticsProvider";

export enum DiscoverScreen {
  DISCOVER_FEED = "DiscoverFeed",
  DISCOVER_ONBOARDING = "DiscoverOnboarding",
  DISCOVER_CONTENT = "DiscoverContent",
  HEADER = "Header",
  REFERRAL_TEMPLATE = "ReferralTemplate",
  LOGIN = "Login",
  NEW_USER_VARIANT = "NewUserVariant",
  RETURNING_USER_VARIANT = "ReturningUserVariant",
  BONUS_GEMS_TEMPLATE = "BonusGemsTemplate",
  CTATemplate = "CTATemplate",
  CLAIM = "Claim",
  CONNECT_TO_CLAIM = "ConnectToClaim",
  PIN_TO_HOME_TEMPLATE = "PinToHomeTemplate",
  QUIZ_TEMPLATE = "QuizTemplate",
  REWARD_POPUP = "RewardPopup",
  GAME_DETAILS = "GameDetails",
  SOCIAL_CONNECTION_TEMPLATE = "SocialConnectionTemplate",
  VIDEO_TEMPLATE = "VideoTemplate",
}

type DiscoverFeedItemType = NonNullable<DiscoverFeedItem>["type"];

// Base properties for any Discover Feed event
interface BaseDiscoverExtras extends BaseAnalyticsExtras {
  contentId: string;
  contentType: DiscoverFeedItemType;
  questId: string;
  gameId?: string;
  gameName?: string;
  priority: number;
  createdAt?: string;
  referrer?: string | null;
  externalAnonId?: string | null;
  categories: string[];
  tags: string[];
  onboardingExperience?: string;
  screen: DiscoverScreen;
}

// Event types discriminated by action
export type DiscoverFeedEvent =
  | {
      action: Action.VIEWED;
      extras: BaseDiscoverExtras & {
        completed?: boolean;
      };
    }
  | {
      action: Action.PRESSED;
      extras: BaseDiscoverExtras & {
        /** UI element pressed ("Option", "ChessBoard", etc.) */
        control: string;
        /** Label of UI element ("Add to Home", "Proceed", etc.) */
        controlLabel: string;
        controlType: ControlType;
        completed?: boolean;
        interactionType: InteractionType;
      } & (
          | {
              contentType: "quiz";
              optionIndex: number;
              correct: boolean;
              totalOptions: number;
            }
          | {
              contentType: Omit<DiscoverFeedItemType, "quiz">;
            }
        );
    }
  | {
      action: Action.WATCHED;
      extras: BaseDiscoverExtras & {
        contentType: "video";
        videoLength: number;
        watchDuration: number;
        watchPercentage: number;
        completed: boolean;
        interactionType: InteractionType;
      };
    }
  | {
      action: Action.EARNED;
      extras: BaseDiscoverExtras & {
        rewardAmount: number;
        /** What action led to earning ("quest", "daily", etc.) */
        completionSource: string;
        interactionType: InteractionType;
      };
    }
  | {
      action: Action;
      extras: BaseDiscoverExtras;
    };

export const useDiscoverAnalytics = () =>
  useAnalyticsTracker<DiscoverFeedEvent>(TrackObject.FEED);
